@file:OptIn(ExperimentalMaterial3Api::class)

package us.laantech.admin

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import us.laantech.admin.api.clearAuthorizationCodeFromUrl
import us.laantech.admin.api.getAuthorizationCodeFromUrl
import us.laantech.admin.pages.LoginView
import us.laantech.admin.menu.accounts.AccountsView
import us.laantech.admin.menu.clients.ClientOnboardView
import us.laantech.admin.menu.clients.ClientListView
import us.laantech.admin.menu.LogsViewer
import us.laantech.admin.menu.clients.CompanyBranchListView
import us.laantech.admin.menu.clients.ads.PosAdSetupView
import us.laantech.admin.menu.clients.products.ManageClientProductsView
import us.laantech.admin.menu.portfolio.ProductPortfolioView
import us.laantech.admin.ui.DateTimeEntryModal
import us.laantech.common.api.KtorClient

data class AppStateUiState(
    val isLoggedIn: Boolean = false,
    val isExchangingCode: Boolean = false,
    val content: @Composable () -> Unit = { ManageClientProductsView() }, // Default page is set here.
)

data class MenuItem(
    val title: String,
    val destination: @Composable () -> Unit,
)

data class MenuSection(
    val title: String,
    val items: List<MenuItem>,
)

fun buildMainMenu(setContent: (@Composable () -> Unit) -> Unit) = listOf(
    MenuSection(
        title = "Debug",
        items = listOf(
            MenuItem(title = "Logs", destination = { LogsViewer() }),
            MenuItem(title = "Date Time Entry", destination = { DateTimeEntryModal(
                onDismiss = {},
                onConfirm = {}
            ) }),
        )
    ),
    MenuSection(
        title = "Account Management",
        items = listOf(
            MenuItem(title = "Accounts", destination = { AccountsView() }),
            MenuItem(title = "Groups", destination = { Text("Item 2") }),
            MenuItem(title = "Roles", destination = { Text("Item 2") }),
        )
    ),
    MenuSection(
        title = "Client Management",
        items = listOf(
            MenuItem(title = "Clients", destination = { ClientListView(onNavigateToOnboarding = { setContent { ClientOnboardView() } }) }),
            MenuItem(title = "Client Onboarding", destination = { ClientOnboardView() }),
            MenuItem(title = "Client Branches", destination = { CompanyBranchListView() }),
            MenuItem(title = "Client Products", destination = { ManageClientProductsView() }),
            MenuItem(title = "Client Devices", destination = { Text("Item 2") }),
            MenuItem(title = "Clients Subscriptions", destination = { Text("Item 2") }),
            MenuItem(title = "POS Ad Setup", destination = { PosAdSetupView() }),
            MenuItem(title = "Payment Terminal", destination = { Text("Item 2") }),
            MenuItem(title = "Client Employees", destination = { Text("Item 2") }),
        )
    ),

    MenuSection(
        title = "Customer Support",
        items = listOf(
            MenuItem(title = "Tickets", destination = { Text("Item 2") }),
            MenuItem(title = "Support Documents", destination = { Text("Item 2") }),
            MenuItem(title = "Survey", destination = { Text("Item 2") }),
        )
    ),

    MenuSection(
        title = "Product Management",
        items = listOf(
            MenuItem(title = "Portfolio", destination = { ProductPortfolioView() }),
        )
    )
)


@Composable
@Preview
fun App(
    viewModel: AppViewModel = viewModel(
        factory = viewModelFactory {
            initializer { AppViewModel() }
        }
    )
) {

    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    AdminPanelTheme {
        if (uiState.isExchangingCode) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    CircularProgressIndicator()
                    Spacer(Modifier.height(16.dp))
                    Text("Signing in...")
                }
            }
        } else if (uiState.isLoggedIn) {
            PermanentNavigationDrawer(
                drawerContent = {
                    PermanentDrawerSheet(
                        modifier = Modifier.widthIn(max = 200.dp),
                        drawerContainerColor = Color.LightGray,
                    ) {
                        Column(Modifier.verticalScroll(rememberScrollState())) {
                            Text("LAAN Admin", modifier = Modifier.padding(16.dp), style = MaterialTheme.typography.titleLarge)

                            buildMainMenu(viewModel::setContent).forEach { section ->
                                HorizontalDivider()
                                Text(section.title, modifier = Modifier.padding(16.dp), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                                section.items.forEach { item ->
                                    NavigationDrawerItem(
                                        label = { Text(item.title) },
                                        selected = false,
                                        onClick = { viewModel.setContent(item.destination) }
                                    )
                                }
                            }

                            HorizontalDivider(modifier = Modifier.padding(top = 16.dp))
                            NavigationDrawerItem(
                                label = { Text("Logout") },
                                selected = false,
                                onClick = { viewModel.logout() }
                            )
                        }
                    }
                },
            ) {
                uiState.content()
            }
        } else {
            LoginView(onLoginSuccess = { viewModel.onLoginSuccess() })
        }
    }
}

class AppViewModel : ViewModel() {

    private val _uiState = MutableStateFlow(AppStateUiState())
    val uiState = _uiState.asStateFlow()

    init {
        // AppViewModel is the first thing App() creates, so this runs before any other
        // ViewModel touches KtorClient.instance.
        KtorClient.configure(ENV)

        // authState is the single source of truth for login state; mirror it into the UI.
        // storeTokens()/clearTokens() (login, refresh, logout, auth failure) all flip it.
        viewModelScope.launch {
            KtorClient.instance.authState.collect { loggedIn ->
                _uiState.value = _uiState.value.copy(isLoggedIn = loggedIn)
            }
        }

        val code = getAuthorizationCodeFromUrl()
        if (code != null) {
            // Web redirect-return path: exchanging the code flips authState on success.
            _uiState.value = _uiState.value.copy(isExchangingCode = true)
            viewModelScope.launch {
                try {
                    KtorClient.instance.exchangeCodeForTokens(code)
                    clearAuthorizationCodeFromUrl()
                } catch (e: Exception) {
                    println("Failed to exchange authorization code: $e")
                    clearAuthorizationCodeFromUrl()
                } finally {
                    _uiState.value = _uiState.value.copy(isExchangingCode = false)
                }
            }
        } else {
            // Silent refresh if the access token expired but the refresh token is still
            // valid; hasValidToken() flips authState via storeTokens() when it succeeds.
            viewModelScope.launch {
                KtorClient.instance.hasValidToken()
            }
        }
    }

    fun setContent(content: @Composable () -> Unit) {
        _uiState.value = _uiState.value.copy(content = content)
    }

    /** Login already flipped authState via exchangeCodeForTokens(); nothing else to do. */
    fun onLoginSuccess() = Unit

    fun logout() {
        // clearTokens() flips authState to false, which the collector mirrors into isLoggedIn.
        KtorClient.instance.clearTokens()
    }

}
